import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import { BREAKPOINT } from '@constants';
import { PageSlice, SliceTabsFeatureDetails, SliceTabsFeatureItem } from '@contracts';
import Colors from '../Colors';
import LinkButton from '../LinkButton';
import useDesktop from '../useDesktop';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface Props {
  slice: PageSlice;
}

const TabsFeature: React.FC<Props> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceTabsFeatureDetails;
  const sliceItems = slice.items as SliceTabsFeatureItem[];
  const [activeTab, setActiveTab] = React.useState(0);
  const isMobile = !useDesktop(BREAKPOINT.mobileM);
  const isTablet = !useDesktop(BREAKPOINT.laptop) && !isMobile;
  const backgroundColor = sliceDetails.background_color;
  const centeredText = sliceDetails.centered_text;

  return (
    <Wrapper $backgroundColor={backgroundColor}>
      <Head $centeredText={centeredText}>
        {sliceDetails.overline && (
          <Typography $variant={TextVariant.UpperText4} as="h3">
            {sliceDetails.overline}
          </Typography>
        )}
        {sliceDetails.heading.richText.length > 0 && (
          <Heading>
            <PrismicRichText field={sliceDetails.heading.richText} />
          </Heading>
        )}

        <TabsRow>
          {sliceItems.map((item: any, index: number) => (
            <TabItem key={index} $isActive={activeTab === index} onClick={() => setActiveTab(index)}>
              {item.icon.url && <img src={item.icon.url} alt="" />}
              <Typography $variant={activeTab === index ? TextVariant.BodyBoldLG : TextVariant.BodyRegularLG}>{item.icon_text}</Typography>
            </TabItem>
          ))}
        </TabsRow>
      </Head>

      <FeatureWrapper
        $itemReverseOrder={sliceItems[activeTab].item_reverse_order}
        $itemReverseOrderMobile={sliceItems[activeTab].item_reverse_order_mobile}
        $itemBackgroundColor={sliceItems[activeTab].background_color}
        $fullWidthBackground={sliceItems[activeTab].background_full_width}
      >
        {(sliceItems[activeTab].image_mobile?.url || sliceItems[activeTab].image?.url) && (
          <Image
            $fullWidthBackground={sliceItems[activeTab].background_full_width}
            src={(isTablet ? sliceItems[activeTab].image_mobile?.url : sliceItems[activeTab].image?.url) ?? ''}
            data-testid="main-image"
          />
        )}
        {sliceItems[activeTab].background_full_width && sliceItems[activeTab].background_image.url && (
          <BackgroundImage src={sliceItems[activeTab].background_image.url} data-testid="bg-image" />
        )}
        <ContentBlock $fullWidthBackground={sliceItems[activeTab].background_full_width}>
          <ContentWrap $itemCenteredText={sliceItems[activeTab].item_centered_text}>
            <HeadBlock>
              {sliceItems[activeTab].item_heading?.richText?.length > 0 && (
                <ItemHeading>
                  <PrismicRichText field={sliceItems[activeTab].item_heading.richText} />
                </ItemHeading>
              )}
              {sliceItems[activeTab].item_subheading?.richText?.length > 0 && (
                <ItemSubheading>
                  <PrismicRichText field={sliceItems[activeTab].item_subheading.richText} />
                </ItemSubheading>
              )}
            </HeadBlock>
            {sliceItems[activeTab].button_url && (
              <LinkButton to={sliceItems[activeTab].button_url} isInline>
                {sliceItems[activeTab].button_text_key}
              </LinkButton>
            )}
          </ContentWrap>
        </ContentBlock>
      </FeatureWrapper>
    </Wrapper>
  );
};

export default TabsFeature;

const Wrapper = styled.div<{ $backgroundColor: string | null }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 40px;
  padding: 64px 80px 96px;
  background-color: ${(props) => props.$backgroundColor || Colors.ecru};
  color: ${Colors.navyBlue};

  & > * {
    max-width: 1200px;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    padding: 56px 64px;
  }

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    padding: 56px 16px;
  }
`;

const Head = styled.div<{ $centeredText: boolean }>`
  display: flex;
  flex-direction: column;
  row-gap: 0px;
  align-items: ${(props) => (props.$centeredText ? 'center' : 'flex-start')};
  width: 100%;
`;

const Heading = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.Heading3]};
    color: inherit;

    @media (max-width: ${BREAKPOINT.tablet}px) {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }

    & > span.italic {
      font-style: italic;
    }
  }
`;

const TabsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;

  @media (max-width: ${BREAKPOINT.tablet}px) {
    gap: 8px;
  }
`;

const TabItem = styled.div<{ $isActive: boolean }>`
  background-color: ${(props) => (props.$isActive ? Colors.paleGold50 : Colors.cream)};
  border-width: 2px;
  border-style: solid;
  border-color: ${(props) => (props.$isActive ? Colors.gold : Colors.ecru)};
  padding: 14px 16px;
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  border-radius: 12px;
  cursor: pointer;

  & > img {
    width: 20px;
    height: 20px;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    padding: 12px 8px;
  }
`;

const FeatureWrapper = styled.div<{
  $itemReverseOrder: boolean;
  $itemReverseOrderMobile: boolean;
  $itemBackgroundColor: string | null;
  $fullWidthBackground: boolean;
}>`
  width: 100%;
  background-color: ${(props) => props.$itemBackgroundColor || Colors.cream};
  display: flex;
  flex-direction: ${(props) => (props.$itemReverseOrder ? 'row-reverse' : 'row')};
  column-gap: 0px;
  align-items: stretch;
  justify-content: center;
  padding: 0px;
  border-radius: 24px;
  position: relative;

  @media (max-width: ${BREAKPOINT.laptopM}px) {
    column-gap: 0;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    flex-direction: ${(props) => (props.$itemReverseOrderMobile ? 'column' : 'column-reverse')};
    min-height: auto;
  }

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    border-radius: 12px;
  }
`;

const BackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  object-fit: cover;
  width: 100%;
  height: 100%;
  z-index: 1;
  border-radius: 24px;

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    border-radius: 12px;
  }
`;

const Image = styled.img<{ $fullWidthBackground: boolean }>`
  max-width: 50%;
  height: auto;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: ${(props) => (props.$fullWidthBackground ? '0px' : '24px')};
  z-index: 2;

  @media (max-width: ${BREAKPOINT.laptopM}px) {
    aspect-ratio: auto;
    min-height: 100%;
  }

  @media (max-width: ${BREAKPOINT.laptop}px) {
    aspect-ratio: 1/1;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    aspect-ratio: ${(props) => (props.$fullWidthBackground ? '1 / 1' : '640 / 420')};
    max-width: 100%;
  }

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    aspect-ratio: 1 / 1;
  }

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    border-radius: ${(props) => (props.$fullWidthBackground ? '0px' : '12px')};
  }
`;

const HeadBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  row-gap: 16px;
`;

const ContentBlock = styled.div<{ $fullWidthBackground: boolean }>`
  display: flex;
  flex-direction: row-reverse;
  flex-grow: 1;
  padding: 72px;
  max-width: 50%;
  z-index: 2;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    padding: ${(props) => (props.$fullWidthBackground ? '40px 24px 24px' : '24px')};
    flex-direction: column;
    max-width: 100%;
  }

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    padding: 32px 16px;
  }
`;

const ContentWrap = styled.div<{ $itemCenteredText: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$itemCenteredText ? 'center' : 'flex-start')};
  justify-content: center;
  row-gap: 24px;
  text-align: ${(props) => (props.$itemCenteredText ? 'center' : 'left')};
  color: ${Colors.navyBlue};
  flex-grow: 1;
  padding: 0;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    row-gap: 16px;
    justify-content: center;
  }
`;

const ItemHeading = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.Heading4]};
    color: inherit;

    @media (max-width: ${BREAKPOINT.laptop}px) {
      ${TEXT_STYLES[TextVariant.Heading6]};
    }

    & > span.italic {
      font-style: italic;
    }
  }
`;

const ItemSubheading = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    color: inherit;

    & > span.bold {
      font-weight: 700;
    }

    @media (max-width: ${BREAKPOINT.mobileM}px) {
      ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    }
  }
`;
